(function () {
  'use strict';

  const MARKERCLUSTERER_SRC = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';

  class StoreLocator {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.stores = this.readStores();
      this.activeFilters = new Set();
      this.userLocation = null;
      this.searchLocation = null;
      this.markers = new Map();
      this.cards = new Map();
      this.map = null;
      this.cluster = null;
      this.mapsReady = false;
      this.initialized = false;

      this.els = {
        mapContainer: root.querySelector('[data-store-locator-map]'),
        list: root.querySelector('[data-store-locator-list]'),
        searchInput: root.querySelector('[data-store-locator-search]'),
        geoBtn: root.querySelector('[data-store-locator-geo]'),
        filters: root.querySelectorAll('[data-store-locator-filter]'),
        countEl: root.querySelector('[data-store-locator-count]'),
        noResults: root.querySelector('[data-store-locator-no-results]'),
      };

      this.countTemplate = (this.els.countEl && this.els.countEl.dataset.countTemplate) || 'Намерени {count} локации';
      this.countTemplateSingular =
        (this.els.countEl && this.els.countEl.dataset.countTemplateSingular) || 'Намерена {count} локация';

      root.querySelectorAll('[data-store-card]').forEach((card) => {
        this.cards.set(card.dataset.storeId, card);
      });
    }

    readConfig() {
      const d = this.root.dataset;
      const [lat, lng] = (d.defaultCenter || '42.7339,25.4858').split(',').map(Number);
      return {
        apiKey: d.apiKey || '',
        country: d.country || 'bg',
        defaultCenter: {lat, lng},
        defaultZoom: Number(d.defaultZoom || 7),
        clustering: d.clustering === 'true',
        markerColor: d.markerColor || '#d3413c',
      };
    }

    readStores() {
      const script = this.root.querySelector('script[data-store-locator-data]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (err) {
        console.error('[store-locator] Failed to parse stores JSON:', err);
        return [];
      }
    }

    async init() {
      if (this.initialized) return;
      this.initialized = true;

      if (!this.config.apiKey) {
        this.showMapError('Google Maps API key липсва. Списъкът остава функционален.');
        return;
      }

      try {
        await this.loadGoogleMapsScript();
      } catch (err) {
        console.error('[store-locator] Google Maps script failed to load:', err);
        this.showMapError('Картата не може да бъде заредена. Опитайте по-късно.');
        return;
      }

      if (this.config.clustering) {
        try {
          await this.loadScript(MARKERCLUSTERER_SRC);
        } catch (err) {
          console.warn('[store-locator] Clusterer failed to load; falling back to un-clustered markers:', err);
          this.config.clustering = false;
        }
      }

      try {
        this.initMap();
        this.initMarkers();
        this.sortByDistance(this.config.defaultCenter);
        this.initAutocomplete();
        this.mapsReady = true;
      } catch (err) {
        console.error('[store-locator] Maps init step failed:', err);
        this.showMapError('Картата не може да бъде заредена. Опитайте по-късно.');
      }
    }

    loadGoogleMapsScript() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }
        const existing = document.querySelector('script[data-google-maps]');
        if (existing) {
          existing.addEventListener('load', resolve, {once: true});
          existing.addEventListener('error', reject, {once: true});
          return;
        }
        const params = new URLSearchParams({
          key: this.config.apiKey,
          libraries: 'places',
          v: 'weekly',
        });
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
        script.async = true;
        script.defer = true;
        script.dataset.googleMaps = 'true';
        script.addEventListener('load', resolve, {once: true});
        script.addEventListener('error', reject, {once: true});
        document.head.appendChild(script);
      });
    }

    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.addEventListener('load', resolve, {once: true});
        script.addEventListener('error', reject, {once: true});
        document.head.appendChild(script);
      });
    }

    initMap() {
      this.map = new google.maps.Map(this.els.mapContainer, {
        center: this.config.defaultCenter,
        zoom: this.config.defaultZoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative',
      });

      const skeleton = this.els.mapContainer.querySelector('.store-locator__map-skeleton');
      if (skeleton) skeleton.remove();
    }

    initMarkers() {
      const markers = [];
      this.stores.forEach((store) => {
        const position = {lat: Number(store.lat), lng: Number(store.lng)};
        if (Number.isNaN(position.lat) || Number.isNaN(position.lng)) return;

        const marker = new google.maps.Marker({
          position,
          map: this.config.clustering ? null : this.map,
          title: store.name,
          icon: this.buildMarkerIcon(store.chainColor || this.config.markerColor),
        });

        marker.addListener('click', () => this.highlightStore(store.id, 'marker'));

        this.markers.set(store.id, marker);
        markers.push(marker);
      });

      if (this.config.clustering && window.markerClusterer && markers.length) {
        this.cluster = new markerClusterer.MarkerClusterer({
          map: this.map,
          markers,
        });
      }

      this.fitMapToVisibleMarkers();
    }

    buildMarkerIcon(color) {
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
          <path d="M16 0C7.2 0 0 7.2 0 16c0 11 16 24 16 24s16-13 16-24c0-8.8-7.2-16-16-16z" fill="${color}"/>
          <circle cx="16" cy="16" r="6" fill="#fff"/>
        </svg>`;
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(32, 40),
        anchor: new google.maps.Point(16, 40),
      };
    }

    fitMapToVisibleMarkers() {
      const visible = Array.from(this.markers.values()).filter((m) => m.getVisible());
      if (!visible.length) return;
      if (visible.length === 1) {
        this.map.setCenter(visible[0].getPosition());
        this.map.setZoom(13);
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      visible.forEach((m) => bounds.extend(m.getPosition()));
      this.map.fitBounds(bounds, 48);
    }

    bindFilters() {
      this.els.filters.forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) {
            this.activeFilters.add(checkbox.value);
          } else {
            this.activeFilters.delete(checkbox.value);
          }
          this.applyFilters();
        });
      });
    }

    applyFilters() {
      const noFiltersActive = this.activeFilters.size === 0;
      let visibleCount = 0;

      this.stores.forEach((store) => {
        const isVisible = noFiltersActive || this.activeFilters.has(store.chainId);
        const card = this.cards.get(store.id);
        const marker = this.markers.get(store.id);

        if (card) card.classList.toggle('is-hidden', !isVisible);
        if (marker) marker.setVisible(isVisible);
        if (isVisible) visibleCount++;
      });

      if (this.cluster) {
        const visibleMarkers = this.stores
          .filter((s) => noFiltersActive || this.activeFilters.has(s.chainId))
          .map((s) => this.markers.get(s.id))
          .filter(Boolean);
        this.cluster.clearMarkers();
        this.cluster.addMarkers(visibleMarkers);
      }

      this.updateResultsCount(visibleCount);

      if (this.els.noResults) {
        this.els.noResults.hidden = visibleCount > 0;
      }
    }

    updateResultsCount(count) {
      if (!this.els.countEl) return;
      const template = count === 1 ? this.countTemplateSingular : this.countTemplate;
      this.els.countEl.textContent = template.replace('{count}', count);
    }

    initAutocomplete() {
      if (!this.els.searchInput || !window.google || !google.maps.places) return;

      const autocomplete = new google.maps.places.Autocomplete(this.els.searchInput, {
        componentRestrictions: {country: this.config.country},
        fields: ['geometry'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) return;
        const loc = place.geometry.location;
        this.searchLocation = {lat: loc.lat(), lng: loc.lng()};
        this.recenterMap(this.searchLocation);
        this.sortByDistance(this.searchLocation);
      });

      this.els.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });
    }

    bindGeolocation() {
      if (!this.els.geoBtn) return;

      this.els.geoBtn.addEventListener('click', () => {
        if (!navigator.geolocation) {
          this.showToast('Браузърът не поддържа геолокация.');
          return;
        }
        this.els.geoBtn.disabled = true;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.userLocation = {lat: pos.coords.latitude, lng: pos.coords.longitude};
            this.recenterMap(this.userLocation);
            this.sortByDistance(this.userLocation);
            this.els.geoBtn.disabled = false;
          },
          (err) => {
            this.els.geoBtn.disabled = false;
            const msg =
              err.code === err.PERMISSION_DENIED
                ? 'Достъп до локацията е отказан.'
                : 'Не успяхме да получим вашата локация.';
            this.showToast(msg);
          },
          {enableHighAccuracy: false, timeout: 10000, maximumAge: 60000},
        );
      });
    }

    recenterMap(coords) {
      if (!this.map) return;
      this.map.setCenter(coords);
      this.map.setZoom(11);
    }

    sortByDistance(origin) {
      const stored = this.stores.map((store) => {
        const distanceKm = this.computeDistanceKm(origin, {lat: store.lat, lng: store.lng});
        return {...store, distanceKm};
      });

      stored.sort((a, b) => a.distanceKm - b.distanceKm);

      const list = this.els.list;
      stored.forEach((store) => {
        const card = this.cards.get(store.id);
        if (!card) return;
        list.appendChild(card);
        const distanceEl = card.querySelector('[data-distance]');
        if (distanceEl) {
          distanceEl.textContent = this.formatDistance(store.distanceKm);
          distanceEl.hidden = false;
        }
      });
    }

    computeDistanceKm(a, b) {
      const R = 6371;
      const toRad = (deg) => (deg * Math.PI) / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);
      const sinDLat = Math.sin(dLat / 2);
      const sinDLng = Math.sin(dLng / 2);
      const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
      return 2 * R * Math.asin(Math.sqrt(h));
    }

    formatDistance(km) {
      if (km < 1) return `${Math.round(km * 1000)} m`;
      if (km < 10) return `${km.toFixed(1)} km`;
      return `${Math.round(km)} km`;
    }

    showToast(message) {
      let toast = this.root.querySelector('[data-store-locator-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'store-locator__toast';
        toast.dataset.storeLocatorToast = '';
        this.root.appendChild(toast);
      }
      toast.textContent = message;
      toast.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
    }

    highlightStore(storeId, source) {
      const card = this.cards.get(storeId);
      if (!card) return;

      this.cards.forEach((c, id) => {
        if (id !== storeId) {
          c.setAttribute('aria-expanded', 'false');
          c.classList.remove('is-active');
        }
      });

      const wasExpanded = card.getAttribute('aria-expanded') === 'true';
      if (source === 'card' && wasExpanded) {
        card.setAttribute('aria-expanded', 'false');
        card.classList.remove('is-active');
        return;
      }
      card.setAttribute('aria-expanded', 'true');
      card.classList.add('is-active');

      if (source === 'marker') {
        card.scrollIntoView({behavior: 'smooth', block: 'nearest'});
      }

      this.panMapToStore(storeId);
    }

    panMapToStore(storeId) {
      if (!this.map) {
        console.warn('[store-locator] Map not ready when card clicked');
        return;
      }

      const marker = this.markers.get(storeId);
      const card = this.cards.get(storeId);
      let position = marker && marker.getPosition ? marker.getPosition() : null;

      if (!position && card) {
        const lat = Number(card.dataset.lat);
        const lng = Number(card.dataset.lng);
        if (!Number.isNaN(lat) && !Number.isNaN(lng) && (lat !== 0 || lng !== 0)) {
          position = new google.maps.LatLng(lat, lng);
        }
      }

      if (!position) {
        console.warn('[store-locator] No valid coordinates for store:', storeId);
        return;
      }

      this.els.mapContainer.scrollIntoView({behavior: 'smooth', block: 'nearest'});

      this.map.setZoom(15);
      this.map.panTo(position);

      if (marker) {
        marker.setAnimation(google.maps.Animation.BOUNCE);
        clearTimeout(this._bounceTimer);
        this._bounceTimer = setTimeout(() => marker.setAnimation(null), 1500);
      }
    }

    bindCardClicks() {
      this.cards.forEach((card, storeId) => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('.store-locator__card-details a, .store-locator__card-details button')) {
            return;
          }
          this.highlightStore(storeId, 'card');
        });
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.highlightStore(storeId, 'card');
          }
        });
        if (!card.hasAttribute('tabindex')) {
          card.setAttribute('tabindex', '0');
        }
      });
    }

    showMapError(message) {
      this.els.mapContainer.innerHTML = `<div class="store-locator__map-error"><p>${message}</p></div>`;
    }
  }

  /* ------------------------- Bootstrap ------------------------- */

  function bootstrap() {
    document.querySelectorAll('[data-store-locator]').forEach((root) => {
      const locator = new StoreLocator(root);

      locator.bindFilters();
      locator.bindGeolocation();
      locator.bindCardClicks();

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                locator.init();
                io.disconnect();
              }
            });
          },
          {rootMargin: '200px'},
        );
        io.observe(root);
      } else {
        locator.init();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
